import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from './Header';
import { LinktreeLogo } from './LinktreeLogo';
import { cn } from '../utils/twUtils';

interface BaseLayoutProps {
  heading?: ReactNode;
  subHeading?: ReactNode;
  headingClasses?: string;
  subHeadingClasses?: string;
  headerClasses?: string;
  mainClasses?: string;
  pageClasses?: string;
  contentWidth?: number | 'full';
  isMobileApp?: boolean;
  hideLogo?: boolean;
}

interface DefaultLayoutProps extends BaseLayoutProps {
  variant?: 'default';
  children: ReactNode;
}

interface InterstitialLayoutProps extends BaseLayoutProps {
  variant?: 'interstitial';
  children?: ReactNode;
}

interface BannerLayoutProps extends BaseLayoutProps {
  variant?: 'banner';
  children?: ReactNode;
  bannerImageDesktop?: string;
  backgroundColor?: string;
}

export type LayoutProps =
  | DefaultLayoutProps
  | InterstitialLayoutProps
  | BannerLayoutProps;

interface ContentLayoutProps extends Omit<BaseLayoutProps, 'pageClasses'> {
  children?: ReactNode;
  variantForCentering?: 'default' | 'interstitial' | 'banner';
}

const ContentLayout = ({
  heading,
  subHeading,
  headingClasses,
  subHeadingClasses,
  headerClasses,
  mainClasses,
  contentWidth = 480,
  children,
  variantForCentering = 'default',
}: ContentLayoutProps): ReactElement => {
  return (
    <>
      <Header
        heading={heading}
        subHeading={subHeading}
        headingClasses={headingClasses}
        subHeadingClasses={subHeadingClasses}
        className={cn(
          headerClasses,
          'mx-auto w-full max-w-[480px] px-6 md:px-0'
        )}
      />
      {children && (
        <main
          className={cn(
            mainClasses,
            'mx-auto w-full flex-grow px-6 pb-[120px] pt-6 md:px-0',
            contentWidth === 'full'
              ? 'max-w-[948px]'
              : `max-w-[${contentWidth}px]`,
            { 'pb-[120px]': variantForCentering !== 'interstitial' }
          )}
        >
          {children}
        </main>
      )}
    </>
  );
};

const Layout = (props: LayoutProps): ReactElement => {
  const { t } = useTranslation();
  const {
    variant = 'default',
    pageClasses,
    children,
    isMobileApp,
    hideLogo = false,
    ...contentProps
  } = props;
  const LogoElement = (
    <div className="block h-[24px] lg:h-[32px] absolute top-6 left-6 lg:top-10 lg:left-10 z-50">
      {isMobileApp ? (
        <LinktreeLogo height="100%" />
      ) : (
        <a
          href="/"
          aria-label={t('Linktree home')}
          className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
        >
          <LinktreeLogo height="100%" />
        </a>
      )}
    </div>
  );
  // Handle banner variant
  if (variant === 'banner') {
    const { bannerImageDesktop, backgroundColor = 'bg-[#225AC0]' } =
      props as BannerLayoutProps;

    return (
      <div
        className={cn(
          pageClasses,
          ' relative flex w-full',
          'h-screen overflow-hidden',
          !isMobileApp ? 'lg:flex-row' : 'flex-col'
        )}
      >
        {!hideLogo && LogoElement}
        <div
          className={cn(
            'flex w-full flex-shrink-0 flex-col',
            'h-full overflow-y-auto',
            !isMobileApp ? 'lg:w-1/2' : 'w-full'
          )}
        >
          <div className="pt-24 lg:pt-20 2xl:pt-32">
            <ContentLayout {...contentProps} variantForCentering="banner">
              {children}
            </ContentLayout>
          </div>
        </div>

        {!isMobileApp && bannerImageDesktop && (
          <div
            className={cn(
              'hidden lg:block lg:h-full lg:w-1/2 lg:flex-shrink-0',
              backgroundColor
            )}
          >
            <img
              src={bannerImageDesktop}
              alt={t('Promotional Banner')}
              className="h-full w-full object-cover"
            />
          </div>
        )}
      </div>
    );
  }

  // Handle default and interstitial variants
  return (
    <div
      className={cn(
        pageClasses,
        ' flex min-h-screen w-full flex-col pt-[28px] lg:pt-32 2xl:pt-64',
        {
          'place-content-center lg:place-content-start':
            variant === 'interstitial',
        }
      )}
    >
      {!hideLogo && LogoElement}
      <ContentLayout {...contentProps} variantForCentering={variant}>
        {children}
      </ContentLayout>
    </div>
  );
};

export default Layout;
